import React, { useEffect, useState } from 'react';
import {
  Calendar, Card, Modal, Form, Input, DatePicker, Button, App,
  List, Tag, Popconfirm, Select, Checkbox, Segmented, Empty,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] });
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import api from '../api/client';

const TYPES = {
  goal:     { label: 'Goal',     color: '#722ed1' },
  task:     { label: 'Task',     color: '#0958d9' },
  homework: { label: 'Homework', color: '#d46b08' },
};
const fmt = (d) => dayjs(d).format('YYYY-MM-DD');

export default function Schedule() {
  const { user }    = useUser();
  const { message } = App.useApp();
  const [items, setItems]       = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(dayjs());
  const [calMode, setCalMode]   = useState('month');
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);   // item being edited, or null for new
  const [saving, setSaving]     = useState(false);
  const [form]                  = Form.useForm();

  const load = async () => {
    try {
      const [iRes, cRes] = await Promise.all([
        api.get('/api/planner'),
        api.get('/api/planner/contacts'),
      ]);
      setItems(iRes.data);
      setContacts(cRes.data);
    } catch { message.error('Failed to load planner'); }
  };

  useEffect(() => { if (user?.userId) load(); }, [user?.userId]);

  const dayItems = items.filter(it => fmt(it.due_date) === selected.format('YYYY-MM-DD'));

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ item_type: 'task', due_date: selected });
    setModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    form.setFieldsValue({
      item_type:       item.item_type,
      title:           item.title,
      description:     item.description,
      due_date:        dayjs(item.due_date),
      shared_with_ids: item.shared_with_ids || [],
    });
    setModal(true);
  };

  const onSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        item_type:       values.item_type,
        title:           values.title,
        description:     values.description || null,
        due_date:        fmt(values.due_date),
        shared_with_ids: values.shared_with_ids || [],
      };
      if (editing) {
        await api.patch(`/api/planner/${editing.item_id}`, payload);
        message.success('Updated');
      } else {
        await api.post('/api/planner', payload);
        message.success('Added to planner');
      }
      setModal(false);
      form.resetFields();
      setEditing(null);
      load();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleDone = async (item) => {
    const next = item.my_status === 'done' ? 'todo' : 'done';
    setItems(prev => prev.map(x => x.item_id === item.item_id ? { ...x, my_status: next } : x));
    try { await api.patch(`/api/planner/${item.item_id}/status`, { status: next }); }
    catch { message.error('Failed to update'); load(); }
  };

  const onDelete = async (id) => {
    try { await api.delete(`/api/planner/${id}`); message.success('Removed'); load(); }
    catch (e) { message.error(e.response?.data?.error || 'Failed to remove'); }
  };

  const dateCellRender = (value) => {
    const day = items.filter(it => fmt(it.due_date) === value.format('YYYY-MM-DD'));
    if (!day.length) return null;
    const seen = [...new Set(day.map(d => d.item_type))];
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 2 }}>
        {seen.map(t => (
          <div key={t} style={{ width: 6, height: 6, borderRadius: '50%', background: TYPES[t]?.color || '#0a0a0a' }} />
        ))}
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Planner</h1>
        <p>Plan goals, tasks and homework — share any item with your {user?.role === 'tutor' ? 'students' : 'tutors'} so it shows on both calendars.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
        <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
          <Calendar
            mode={calMode}
            cellRender={dateCellRender}
            onSelect={(date, { source }) => {
              if (calMode === 'year' && source === 'month') { setSelected(date); setCalMode('month'); }
              else if (calMode === 'month' && source === 'date') { setSelected(date); }
            }}
            onPanelChange={(date, mode) => { setSelected(date); setCalMode(mode); }}
            style={{ fontSize: 13 }}
            headerRender={({ value, onChange }) => {
              const isYear = calMode === 'year';
              const label  = isYear ? value.format('YYYY') : value.format('MMMM YYYY');
              const unit   = isYear ? 'year' : 'month';
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px 12px' }}>
                  <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => onChange(value.subtract(1, unit))} />
                  <span style={{ fontWeight: 600, fontSize: 14, cursor: isYear ? 'default' : 'pointer' }}
                    onClick={() => { if (!isYear) setCalMode('year'); }}>
                    {label}
                  </span>
                  <Button type="text" size="small" icon={<RightOutlined />} onClick={() => onChange(value.add(1, unit))} />
                </div>
              );
            }}
          />
          <div style={{ display: 'flex', gap: 16, marginTop: 8, paddingLeft: 4 }}>
            {Object.entries(TYPES).map(([k, v]) => (
              <span key={k} style={{ fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: v.color, display: 'inline-block' }} />
                {v.label}
              </span>
            ))}
          </div>
        </Card>

        <div>
          <Card
            variant="outlined" style={{ borderColor: '#e8e8e8' }}
            title={<span style={{ fontSize: 13 }}>{selected.format('ddd, D MMM YYYY')}</span>}
            extra={<Button size="small" type="primary" icon={<PlusOutlined />} onClick={openNew}
              style={{ background: '#0a0a0a', border: 'none', fontSize: 12 }}>Add</Button>}
          >
            {dayItems.length === 0
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing planned for this day" style={{ padding: '16px 0' }} />
              : (
                <List
                  dataSource={dayItems}
                  renderItem={(item) => {
                    const t = TYPES[item.item_type] || TYPES.task;
                    const done = item.my_status === 'done';
                    return (
                      <List.Item style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                          <Checkbox checked={done} onChange={() => toggleDone(item)} style={{ marginTop: 2 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 500, fontSize: 13, textDecoration: done ? 'line-through' : 'none', color: done ? '#aaa' : '#0a0a0a' }}>
                                {item.title}
                              </span>
                              <Tag style={{ fontSize: 10, color: t.color, borderColor: t.color, background: 'transparent', marginInlineEnd: 0 }}>
                                {t.label}
                              </Tag>
                            </div>
                            {item.description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{item.description}</div>}
                            <div style={{ fontSize: 11, color: '#bbb', marginTop: 3 }}>
                              {item.is_mine
                                ? (item.recipient_count > 0
                                    ? `Shared with ${item.shared_with_names} · ${item.done_count}/${item.recipient_count} done`
                                    : 'Private')
                                : `From ${item.owner_name}`}
                            </div>
                          </div>
                          {item.is_mine && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                              <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(item)} />
                              <Popconfirm title="Remove this item?" onConfirm={() => onDelete(item.item_id)}>
                                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )
            }
          </Card>
        </div>
      </div>

      <Modal title={editing ? 'Edit item' : 'Add to planner'} open={modal} maskClosable={false}
        onCancel={() => { setModal(false); form.resetFields(); setEditing(null); }} footer={null}>
        <Form form={form} layout="vertical" onFinish={onSave} style={{ marginTop: 16 }} requiredMark={false}
          initialValues={{ item_type: 'task' }}>
          <Form.Item label="Type" name="item_type" rules={[{ required: true }]}>
            <Segmented options={Object.entries(TYPES).map(([value, v]) => ({ value, label: v.label }))} block />
          </Form.Item>
          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Enter a title' }]}>
            <Input placeholder="e.g. Finish Paper 2 practice / Hit 80% on Algebra" />
          </Form.Item>
          <Form.Item label="Date" name="due_date" rules={[{ required: true, message: 'Pick a date' }]}>
            <DatePicker style={{ width: '100%' }} format="D MMM YYYY" />
          </Form.Item>
          <Form.Item label="Details" name="description">
            <Input.TextArea rows={3} placeholder="Optional notes" />
          </Form.Item>
          <Form.Item label="Share with (optional)" name="shared_with_ids"
            extra={contacts.length === 0 ? 'No active tutors/students to share with yet.' : 'Each recipient tracks their own completion.'}>
            <Select mode="multiple" allowClear placeholder="Keep private"
              options={contacts.map(c => ({ value: c.user_id, label: `${c.name} (${c.role_name})` }))} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={saving} style={{ background: '#0a0a0a', border: 'none' }}>
            {editing ? 'Save changes' : 'Add to planner'}
          </Button>
        </Form>
      </Modal>
    </AppLayout>
  );
}
